import {
  Box,
  FormControl,
  FormLabel,
  HStack,
  Icon,
  Spinner,
  Stack,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tooltip,
  Tr,
  useToast,
} from '@chakra-ui/react';
import download from 'downloadjs';
import React, { useContext, useState } from 'react';
import { TbFileSearch } from 'react-icons/all';
import { MultiSelect } from 'react-multi-select-component';
import { Link } from 'react-router-dom';
import {
  Button,
  Datepicker,
  Heading,
  Select,
  StatusBadge,
  Table,
} from '~/components';
import { Card } from '~/components/Card';
import { FilterContext } from '~/contexts/FilterContext/FilterContext';
import { useCompanyGroupsAsSelect } from '~/hooks';
import { getInsurances, getInsuranceXlsx } from '~/services/Insurance';
import { SelectItem } from '~/types';
import { InsuranceList } from '~/types/insurance';
import { getLastDayOfMonthAfterXMonths } from '~/utils/getLastDayOfMonthAfterXMonths';
import { MultiSelectStrings } from '~/utils/multiSelectStrings';

export const Insurance: React.VFC = () => {
  const toast = useToast();

  const [selectedGroups, setSelectedGroups] = useState<SelectItem[]>([]);

  const [isLoadingDownload, setIsLoadingDownload] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const [dataInicial, setDataInicial] = useState<Date | undefined>(() => {
    const data = new Date();
    return data || undefined;
  });
  const [dataFinal, setDataFinal] = useState<Date | undefined>(() => {
    return getLastDayOfMonthAfterXMonths(new Date());
  });

  const [maxDataFinal, setMaxDataFinal] = useState<Date | undefined>(() => {
    return getLastDayOfMonthAfterXMonths(new Date());
  });

  const [st_seguro, setStSeguro] = useState<string>('');

  const { groups: groupsFilter } = useContext(FilterContext);

  const { data: groups, isLoading: isGroupsLoading } = useCompanyGroupsAsSelect(
    {},
    9999,
    groupsFilter,
  );

  const [seguros, setSeguros] = useState<InsuranceList[] | undefined>(
    undefined,
  );

  const handleList = async () => {
    setIsLoading(true);
    const request = getInsurances(
      selectedGroups.length > 0 ? selectedGroups : groupsFilter,
      st_seguro,
      dataInicial,
      dataFinal,
    );

    request
      .then(r => {
        setSeguros(r);
      })
      .catch(async err => {
        toast({
          title: `Erro`,
          description: err.response.data.detail,
          status: 'error',
          duration: 3000,
          isClosable: true,
          position: 'top-right',
        });
      })
      .finally(() => setIsLoading(false));
  };
  const handleDownload = async () => {
    setIsLoadingDownload(true);
    getInsuranceXlsx(
      selectedGroups.length > 0 ? selectedGroups : groupsFilter,
      st_seguro,
      dataInicial,
      dataFinal,
    )
      .then(r => {
        if (r.data.size > 0) {
          download(
            new Blob([r.data], { type: r.data.type }),
            r.headers['content-disposition'].match(/filename="(.+)"/)[1],
          );
        } else {
          toast({
            title: r?.data?.error,
            description: 'Entre em contato para mais informações',
            status: 'error',
            duration: 3000,
            isClosable: true,
            position: 'top-right',
          });
        }
      })
      .catch(async err => {
        const error2 = JSON.parse(await err.response.data.text());

        toast({
          title: `Erro`,
          description: error2.detail,
          status: 'error',
          duration: 3000,
          isClosable: true,
          position: 'top-right',
        });
      })
      .finally(() => setIsLoadingDownload(false));
  };

  const handlePeriodoInicial = (date: Date | null) => {
    let data_final = date;
    if (data_final) {
      data_final = getLastDayOfMonthAfterXMonths(data_final, 12);
    }
    setDataInicial(date ?? undefined);
    setDataFinal(data_final ?? undefined);
    setMaxDataFinal(data_final ?? undefined);
  };

  const handlePeriodoFinal = (date: Date | null) => {
    setDataFinal(date ?? undefined);
  };

  const handleSelectedGroupsChange = async (itens?: SelectItem[]) => {
    setSelectedGroups(itens || []);
  };

  const handleStSeguroChange = (e: string) => {
    setStSeguro(e);
  };

  return (
    <Box px="8" py="4">
      <Heading>Seguros</Heading>
      <HStack pt={2} w="100%" align="end" mt={3} mb={5}>
        <HStack align="end" gridGap={2} width="40%">
          <Datepicker
            label="Período de contratação"
            required
            onChange={e => handlePeriodoInicial(e)}
            selectedDate={dataInicial || undefined}
            isClearable
          />
          <Text>a</Text>
          <Datepicker
            onChange={e => handlePeriodoFinal(e)}
            selectedDate={dataFinal || undefined}
            isClearable
            minDate={dataInicial || undefined}
            maxDate={maxDataFinal}
            isDisabled={!dataInicial}
          />
        </HStack>
        <FormControl width="30%">
          <Select
            name="st_seguro"
            label="Situação do seguro"
            fontSize="14px"
            whiteSpace="nowrap"
            value={st_seguro}
            onChange={e => handleStSeguroChange(e.target.value)}
          >
            <option value="">Todos</option>
            <option value="0">À vencer</option>
            <option value="1">Vigente</option>
            <option value="2">Encerrado</option>
          </Select>
        </FormControl>
        <FormControl width="30%">
          <FormLabel fontSize="14px">Grupo de Clientes</FormLabel>
          <MultiSelect
            value={selectedGroups}
            options={groups || []}
            onChange={(changes: SelectItem[]) =>
              handleSelectedGroupsChange(changes)
            }
            labelledBy="Selecione"
            overrideStrings={MultiSelectStrings}
            isLoading={isGroupsLoading}
          />
        </FormControl>
        <Button
          marginTop="auto"
          disabled={isLoading || !dataInicial || !dataFinal}
          onClick={handleList}
          color="white"
          background="gray.900"
          _hover={{
            bgColor: 'black',
          }}
          width="150px"
        >
          Consultar
          {isLoading && <Spinner style={{ marginLeft: '10px' }} />}
        </Button>
        {seguros !== undefined && seguros.length > 0 && (
          <Button
            marginTop="auto"
            disabled={isLoadingDownload || !dataInicial || !dataFinal}
            onClick={handleDownload}
            variant="secondary"
            width="150px"
          >
            Exportar
            {isLoadingDownload && <Spinner style={{ marginLeft: '10px' }} />}
          </Button>
        )}
      </HStack>
      {seguros === undefined ? (
        <Card
          width="100%"
          content={
            <Stack alignItems="center" gridGap={2} m={5}>
              <Icon as={TbFileSearch} fontSize="50px" />
              <Text textAlign="center">
                Selecione um período para gerar o relatório.
              </Text>
            </Stack>
          }
        />
      ) : (
        <Table type="secondary" size="sm" marginStart="0px">
          <Thead>
            <Tr>
              <Th>N° Contrato</Th>
              <Th>N° Pedido Locação</Th>
              <Th width="500px">Empresa/Grupo</Th>
              <Th textAlign="right">Qtd. Itens</Th>
              <Th width="95px">N° da Apólice</Th>
              <Th textAlign="center">Data de início</Th>
              <Th textAlign="center">Data de encerramento</Th>
              <Th textAlign="center">Status da apólice</Th>
            </Tr>
          </Thead>
          <Tbody>
            {seguros.length ? (
              seguros.map(info => (
                <Tr>
                  <Td fontWeight="bold">
                    <Link to={`/portal/contracts/${info.id_contrato}`}>
                      <Text
                        _hover={{
                          color: 'gray.900',
                          textDecoration: 'underline',
                        }}
                      >
                        {info.nu_contrato}
                      </Text>
                    </Link>
                  </Td>
                  <Td>{info.nu_pedido}</Td>
                  <Td>
                    <Tooltip
                      hasArrow
                      label={info.nm_razaosocial || ''}
                      bg="black"
                      shouldWrapChildren
                    >
                      <Text fontWeight="bold" noOfLines={1}>
                        {info.nu_cnpj} | {info.nm_razaosocial || '-'}
                      </Text>
                    </Tooltip>
                    <Text noOfLines={1}>{info.nm_grupoeconomico}</Text>
                  </Td>
                  <Td textAlign="right">{info.qtd_equipamentos}</Td>
                  <Td>{info.nu_apolice}</Td>
                  <Td textAlign="center">{info.formattedStartDate}</Td>
                  <Td textAlign="center">{info.formattedEndDate}</Td>
                  <Td textAlign="center">
                    <StatusBadge variant={info.statusColor} width="fit-content">
                      {info.formattedStatus}
                    </StatusBadge>
                  </Td>
                </Tr>
              ))
            ) : (
              <Tr>
                <Td colSpan={8}>
                  <Text textAlign="center">Nenhum registro encontrado</Text>
                </Td>
              </Tr>
            )}
          </Tbody>
        </Table>
      )}
    </Box>
  );
};
